/**
 * Factory functions for common table row actions.
 *
 * Usage:
 *   import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'
 *
 *   actionsConfig: [
 *     showDetailsAction(this.$t, (event) => this.showEdit(event)),
 *     activationAction(this.$t, (event) => this.onActivation(event), (event) => !event?.data?.deactivated_at),
 *     cancelAction(this.$t, (event) => this.onCancel(event), { visible: (event) => this.canCancel(event) }),
 *     deleteAction(this.$t, (event) => this.onDelete(event)),
 *   ]
 *
 * Every factory spreads `attrs` last, so a caller can override any of what it sets -
 * a record-specific label, a `visible` predicate, a different icon.
 */

export const showDetailsAction = (t, onClick, attrs = {}) => ({
  label: t('base.show_details'),
  icon: 'mdi-eye',
  click: onClick,
  ...attrs
})

/**
 * Calling a record off without deleting it - a record that was raised and then called
 * off is still part of the record, so it stays in the listing under a cancelled status.
 *
 * The generic `base.cancel` label is what a listing gets by default; a screen whose
 * record has a name of its own ("Otkaži nalog") passes its own `label` through `attrs`,
 * along with the `visible` predicate deciding which rows can still be called off.
 */
export const cancelAction = (t, onClick, attrs = {}) => ({
  label: t('base.cancel'),
  icon: 'mdi-close-circle-outline',
  click: onClick,
  ...attrs
})

export const deleteAction = (t, onClick, attrs = {}) => ({
  label: t('base.delete'),
  src: '/img/icons/delete-action-icon.svg',
  click: onClick,
  ...attrs
})

/**
 * @param {Function} t - translation function (this.$t)
 * @param {Function} onClick - click handler
 * @param {Function} isActive - receives the action event, returns true if the record is currently active.
 *   Examples:
 *     (event) => !event?.data?.deactivated_at
 *     (event) => !!event?.data?.active
 *     (event) => !!event?.data?.status
 */
export const activationAction = (t, onClick, isActive, attrs = {}) => ({
  label: (event) => isActive(event) ? t('base.deactivate') : t('base.activate'),
  src: (event) => isActive(event)
    ? '/img/icons/deactivate-action-icon.svg'
    : '/img/icons/activate-action-icon.svg',
  click: onClick,
  ...attrs
})
